import { FileUploader } from 'react-drag-drop-files';
import { ReactNode, useState } from 'react';
import { Container, MobileUploadButton, Wrapper } from './styles';
import { Controller, useFormContext } from 'react-hook-form';
import styled from '@emotion/styled';
import { RecordFilter, Recording } from '@/types/api';
import { useSetAudioFile, useUploadFile } from '@/hooks/apiHooks';
import UploadButton from '@/components/Record/UploadButton';
import { formatToHHMMSS } from '@/utils/function';
import { getAudioDuration } from '@/components/Record/util/function';
import { useTranslation } from 'react-i18next';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import {
  ATTACH_GIVEN_UP_MESSAGE,
  attachFailedDialogOptions,
} from '@/components/Record/util/attachFailedDialog';
import {
  getPrevPageArea,
  useGTMEventTracker,
} from '@/hooks/gtm/useGTMEventTracker';
import { GTMEventName } from '@/constant/gtm';
import { getDocumentDetailParam } from '@/utils/gtm/work';
import usePlatform from '@/components/Record/hooks/usePlatform';
import UploadIcon from '@/public/icons/document/upload.svg';
import { useSetAtom } from 'jotai';
import { uploadingState } from '@/recoil/atom';

const MAX_FILE_SIZE = 500; // 500MB

const fileTypes = [
  'AAC',
  'AVI',
  'FLAC',
  'M4A',
  'MKV',
  'MOV',
  'MP3',
  'MP4',
  'OGG',
  'WAV',
  'WEBM',
];

type P = {
  children: ReactNode;
};

const DragDropFiles = ({ children }: P) => {
  const eventTracker = useGTMEventTracker();
  const { control, getValues, watch } = useFormContext<RecordFilter>();
  const [_error, setError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useTranslation();
  const dialog = useDialog();
  const { showError } = useSnackbar();
  const setUploading = useSetAtom(uploadingState);

  const files = watch('recordings');

  const { isMobilePath } = usePlatform();

  return (
    <Controller
      name={'recordings'}
      control={control}
      render={({ field }) => {
        const handleChange = async (newFiles: File[] | FileList | File) => {
          // 올리기 시작한 문서 — 연결 API 는 늘 이 문서로 보낸다. 올리는 사이 다른 문서로 옮겨 가면
          // 폼은 그 문서 것이라 건드리지 않는다(새 문서에 붙고 자동 정리까지 돌았다 — PORTAL-12588)
          const docId = getValues('docId');
          const isSameDoc = () => getValues('docId') === docId;
          setIsUploading(true);
          const filesArray = Array.isArray(newFiles)
            ? newFiles
            : newFiles instanceof FileList
            ? Array.from(newFiles)
            : [newFiles];
          setUploading((prev) => ({
            ...prev,
            record: [
              ...prev.record,
              ...filesArray.map((f) => ({ name: f.name, percent: 0 })),
            ],
          }));
          eventTracker(GTMEventName.clickPostAction, {
            page_area: getPrevPageArea(),
            area_name: '문서',
            action_type: '음성 파일 업로드',
            ...getDocumentDetailParam(getValues()),
          });
          // 연결만 다시 시도 — 업로드는 끝났다. 그사이 목록이 바뀌었을 수 있어 최신 값에 붙인다
          const attach = async (recording: Recording) => {
            await useSetAudioFile(docId, {
              name: recording.name,
              uuid: recording.uuid,
              duration: recording.duration,
            });
            if (!isSameDoc()) return;
            const latest = getValues('recordings');
            if (!latest.some((r) => r.uuid === recording.uuid)) {
              field.onChange([...latest, recording]);
            }
          };
          const showAttachFailed = (recording: Recording): void =>
            dialog.show(
              attachFailedDialogOptions({
                uuid: recording.uuid,
                onRetry: () =>
                  attach(recording).catch(() => showAttachFailed(recording)),
                onGiveUp: () =>
                  showError({ message: t(ATTACH_GIVEN_UP_MESSAGE) }),
              }),
            );

          let currentValues = field.value;
          for (const file of filesArray) {
            let uploaded: Recording | null = null;
            try {
              const uploadData = await useUploadFile(
                file,
                true,
                undefined,
                ({ percent }) =>
                  setUploading((prev) => ({
                    ...prev,
                    record: prev.record.map((item) =>
                      item.name === file.name ? { ...item, percent } : item,
                    ),
                  })),
              );
              const duration = await getAudioDuration(file);
              const newFile: Recording = {
                name: uploadData.data.fileName,
                uuid: uploadData.data.uuid,
                duration: formatToHHMMSS(duration),
                state: 'complete',
              };
              uploaded = newFile;
              await useSetAudioFile(docId, {
                name: newFile.name,
                uuid: newFile.uuid,
                duration: newFile.duration,
              });

              if (isSameDoc()) {
                currentValues = [...currentValues, newFile];
                field.onChange(currentValues);
              }
            } catch (e) {
              console.error(e);
              // 업로드는 됐고 연결만 실패 — 녹음과 같은 「다시 시도」 안내 (PORTAL-12588 QA 5번)
              if (uploaded) {
                showAttachFailed(uploaded);
                continue;
              }
              dialog.show({
                cancelButtonText: t('닫기'),
                confirmButtonText: t('오류 보고'),
                message: t(
                  '파일이 정상적으로 업로드되지 않았어요.\n확인을 위해 오류 보고에 문서 링크를 함께 첨부해 주세요.',
                ),
                onConfirm: () => {
                  window.open(
                    'https://fnf.atlassian.net/servicedesk/customer/portal/50/group/154/create/813',
                    '_blank',
                    'noopener,noreferrer',
                  );
                },
              });
            } finally {
              setUploading((prev) => ({
                ...prev,
                record: prev.record.filter((item) => item.name !== file.name),
              }));
            }
          }
          setIsUploading(false);
        };

        return (
          <Container mobileMode={isMobilePath}>
            {isMobilePath && children}
            <FileUploader
              handleChange={handleChange}
              name="file"
              types={fileTypes}
              multiple={true}
              hoverTitle={'영상/음성 파일만 첨부할 수 있습니다.'}
              dropMessageStyle={{
                backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                border: 'none',
              }}
              maxSize={MAX_FILE_SIZE}
              onSizeError={() => setError(true)}
              onTypeError={() => setError(true)}
              disabled={isUploading}
            >
              <SubContainer mobileMobe={isMobilePath}>
                {!isMobilePath && (
                  <div
                    style={{
                      color: 'var(--dk-text-dim, #B7BBC2)',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {t(
                      '음성 또는 영상 파일을 이곳에 추가해 주세요. (개당 최대 500MB)',
                    )}
                  </div>
                )}
                <Wrapper>
                  {!isMobilePath && (
                    <UploadButton
                      isUploading={isUploading}
                      title={t('음성 파일 업로드')}
                      style={{ width: '116px', height: '36px' }}
                    />
                  )}
                  {(!isMobilePath || (isMobilePath && files.length === 0)) && (
                    <div
                      style={{
                        fontSize: isMobilePath ? '14px' : '12px',
                        color: isMobilePath ? '#B7BBC2' : '#888D96',
                        lineHeight: isMobilePath ? '24px' : '18px',
                      }}
                    >
                      {t(
                        '파일을 업로드 하거나 녹음을 시작하면 참석자 모두 동의한 것으로 간주됩니다. 내용에 개인정보를 포함하지 않도록 주의해 주세요.',
                      )}
                    </div>
                  )}
                </Wrapper>
                {isMobilePath && (
                  <MobileUploadButton>
                    <UploadIcon />
                    <div>음성 파일 업로드</div>
                  </MobileUploadButton>
                )}
              </SubContainer>
            </FileUploader>
            {!isMobilePath && children}
          </Container>
        );
      }}
    />
  );
};

export default DragDropFiles;

const SubContainer = styled.div<{ mobileMobe: boolean }>`
  padding: ${({ mobileMobe }) => (mobileMobe ? '' : '10px 16px 10px 12px')};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
